import { RadioSelectableCard, RadioSelectableCardProps, Typography } from '@qbe/ads-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ExcessErrorModal from '~/components/ExcessErrorModal';
import {
  Option,
  useAppContent,
  useAppDispatch,
  useAppNavigate,
  useCoverDetails,
  useOptionSelect,
  useQuote,
  useSelectCover,
} from '~/hooks';
import { updateLevelOfExcess } from '~/slices/quote';
import { qalFormStartAction } from '~/thunks/analytics';
import { quotePremiumsAction } from '~/thunks/quotePremiumsAction';
import { Cover } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

interface LevelOfExcessProps {
  setIsLoadingPremiums: (val: boolean) => void;
}

const options: Option[] = [
  {
    id: '500',
    value: '500',
  },
  {
    id: '1000',
    value: '1000',
  },
];

const LevelOfExcess: React.FC<LevelOfExcessProps> = ({ setIsLoadingPremiums }) => {
  const { getTextContent: getSharedText } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent: getTextContentModal } = useAppContent({
    basePath: 'dsme.quoteModal',
  });

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { levelOfExcess } = useQuote();
  const { completedCovers } = useSelectCover();
  const { buildingSI, contentsSI, grossIncome, theftContentsSI, tobaccoSI } = useCoverDetails();
  const { selectedIDs, onSelectID, setSelectedIDs } = useOptionSelect({
    options,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [excess, setExcess] = useState<number | null>(null);
  const [openExcessError, setOpenExcessError] = useState(false);

  const handleFormStart = useCallback(() => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  }, [dispatch]);

  useEffect(() => {
    levelOfExcess && setSelectedIDs([String(levelOfExcess)]);
  }, [levelOfExcess, setSelectedIDs]);

  useEffect(() => {
    setIsLoadingPremiums(isLoading);
  }, [isLoading, setIsLoadingPremiums]);

  useEffect(() => {
    const update = async () => {
      setIsLoading(true);
      await dispatch(quotePremiumsAction());
      setIsLoading(false);
    };
    if (excess && excess !== levelOfExcess && !isLoading) {
      dispatch(updateLevelOfExcess(excess));
      update().then();
    }
  }, [dispatch, excess, isLoading, levelOfExcess]);

  const onChange = useCallback(
    (value: string) => {
      handleFormStart();
      const selectedExcess = Number(value);
      const isExcessError =
        (buildingSI &&
          buildingSI < selectedExcess &&
          completedCovers.includes(Cover.BuildingAndContents)) ||
        (contentsSI &&
          contentsSI < selectedExcess &&
          completedCovers.includes(Cover.BuildingAndContents)) ||
        (grossIncome &&
          grossIncome < selectedExcess &&
          completedCovers.includes(Cover.BusinessInterruption)) ||
        (theftContentsSI &&
          theftContentsSI < selectedExcess &&
          completedCovers.includes(Cover.Theft)) ||
        (tobaccoSI && tobaccoSI < selectedExcess && completedCovers.includes(Cover.Theft));

      if (isExcessError) {
        setOpenExcessError(true);
      } else {
        navigate('#indicative-quote-value');
        if (!selectedIDs.includes(value)) {
          onSelectID(value);
          setExcess(Number(value));
        }
      }
    },
    [
      buildingSI,
      completedCovers,
      contentsSI,
      handleFormStart,
      grossIncome,
      navigate,
      onSelectID,
      selectedIDs,
      theftContentsSI,
      tobaccoSI,
    ]
  );

  const radioList = useMemo(
    () =>
      options.map(({ id, value }) => ({
        id,
        value,
        label: currencyFormatter(value, CurrencyFormat.Comma),
        labelProps: {
          variant: 'label2',
          weight: 'bold',
        },
      })) as RadioSelectableCardProps['radioList'],
    []
  );

  return (
    <>
      <RadioSelectableCard
        title=""
        direction="horizontal"
        data-track="level of Excess"
        data-track-value="capture"
        name="levelOfExcess"
        radioList={radioList}
        defaultValue={levelOfExcess}
        onChange={onChange}
        disabled={isLoading}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        sx={{
          '& .selectable-card_radio-group': {
            // TODO - https://qbe-appservices.atlassian.net/browse/AD1-463
            display: 'inline-flex',
          },
        }}
      />

      <ExcessErrorModal
        open={openExcessError}
        onContinue={() => {
          setOpenExcessError(false);
        }}
        onCancel={() => {
          window.location.href = getSharedText('qbeHome');
        }}
      >
        <Typography>{getTextContentModal('content.levelOfExcess.description')}</Typography>
      </ExcessErrorModal>
    </>
  );
};

export default LevelOfExcess;
